import { createComponent } from '@workday/canvas-kit-react/common';
import { Visibility } from '@workday/canvas-kit-react/disclosure';
import { Box, BoxProps } from '@workday/canvas-kit-react/layout';
import { system } from '@workday/canvas-tokens-web';
import { WcpStyles } from '@workday/wcp-tokens';
import React, { createContext, useCallback, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Canvas } from '../../Canvas';
import wconsole from '../../util/wconsole';
import ConfirmationPopupContent from './ConfirmationPopupContent';
import { PopupContextModal } from './PopupContextModal';

export enum ModalWidth {
  s = '440px',
  m = '800px',
}

export enum ModalHeight {
  m = '50vh',
  l = '75vh',
}

export interface PopupContextModel {
  confirm: (content: React.ReactNode, options?: ConfirmModalOptions) => void;
  display: (content: React.ReactNode, options?: ModalOptions) => void;
  back: () => void;
  closeAll: () => void;
}

export interface ModalOptions {
  modelOptions?: ModelOptions;
  heading?: string | React.ReactElement<typeof Canvas.Modal.Heading>;
  // use when designs call for a popup heading that has a high emphasis part followed by a low emphasis second part, like: *Copy App* from App Catalog
  // only supported if `heading` is a string, not when it's a react element
  headingLowEmphasis?: string;
  // Request blocking modal (no overlay/Escape dismiss, default Canvas X hidden). Honored only when TOGGLE_BLOCKING_POPUPS is on.
  blocking?: boolean;
  hideDefaultCloseIcon?: boolean;
  // don't close popup when the route changes
  persistOnRouteChange?: boolean;
  overflow?: Visibility; // required when a form element (like a dropdown) needs to extend beyond the Modal -- should be set to 'visible' in that case
  padding?: number | (string & {});
  width?: ModalWidth; // defaults to 's'
  height?: ModalHeight;
  maxHeight?: ModalHeight;
  /**
   * Overrides default modal card background (e.g. About page dark surface). Omit for theme default (`bg.default`).
   */
  cardBackgroundColor?: string;
}

export interface ModelOptions {
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  initialVisibility?: Visibility;
  onHide?: () => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  shouldHide?: () => boolean;
}

export interface ConfirmModalOptions extends ModalOptions {
  checkboxConfirmText?: string;
  customConfirmText?: string;
  /** Renders a destructive delete button instead of the default primary confirm button. */
  destructive?: boolean;
  onCancel?: () => void;
  onConfirm: () => void;
}

export const PopupContext = createContext<PopupContextModel>({
  confirm: wconsole.log,
  display: wconsole.log,
  back: () => undefined,
  closeAll: () => undefined,
});

type PopupHistoryBackAction = {
  type: 'back';
};

type PopupHistoryRenderAction = {
  type: 'render';
  content: React.ReactElement<typeof Canvas.Modal>;
};

type PopupHistoryAction = PopupHistoryBackAction | PopupHistoryRenderAction | null;

interface PopupHistoryReducer {
  current?: React.ReactElement<typeof Canvas.Modal> | null;
  modalHistory?: Array<React.ReactElement<typeof Canvas.Modal>>;
}

const popupHistoryReducer: React.Reducer<PopupHistoryReducer, PopupHistoryAction> = (
  state,
  action
): PopupHistoryReducer => {
  if (action === null)
    return {
      current: null,
      modalHistory: [],
    };

  if (action?.type === 'back') {
    const newState: PopupHistoryReducer = { modalHistory: [...(state?.modalHistory ?? [])] };
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    newState.current = newState.modalHistory.pop();
    return newState;
  }

  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  return { current: action.content, modalHistory: [...(state?.modalHistory ?? []), state.current] };
};

export const PopupProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [{ current: currentModal }, updateModalHistory] = useReducer(popupHistoryReducer, {
    current: null,
    modalHistory: [],
  });
  const [persistOnRouteChange, setCloseOnRouteChange] = useState<boolean | null>(null);
  const location = useLocation();
  const path = useRef<string>(location.pathname);

  const closeAll = useCallback(() => updateModalHistory(null), []);
  const goBack = useCallback(() => updateModalHistory({ type: 'back' }), []);
  const confirm = useCallback(
    (content: React.ReactNode, options?: ConfirmModalOptions) => {
      updateModalHistory({
        type: 'render',
        content: (
          <PopupContextModal closeAll={closeAll} options={options}>
            {/* @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation */}
            <ConfirmationPopupContent {...options} message={content} />
          </PopupContextModal>
        ),
      });
    },
    [closeAll]
  );
  const display = useCallback(
    (content: React.ReactNode, options?: ModalOptions) => {
      updateModalHistory({
        type: 'render',
        content: (
          <PopupContextModal closeAll={closeAll} options={options}>
            {content}
          </PopupContextModal>
        ),
      });
      // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
      setCloseOnRouteChange(options?.persistOnRouteChange);
    },
    [closeAll]
  );

  useLayoutEffect(() => {
    if (path.current !== location.pathname) {
      path.current = location.pathname;
      if (!persistOnRouteChange) closeAll();
    }
  }, [location.pathname, persistOnRouteChange]);

  const context = useMemo(() => ({ confirm, display, back: goBack, closeAll }), [confirm, display, goBack, closeAll]);

  return (
    <PopupContext.Provider value={context}>
      {children}
      {currentModal}
    </PopupContext.Provider>
  );
};

export const withPopupContext =
  <T extends any>( // eslint-disable-line @typescript-eslint/no-explicit-any -- Grandfathered in
    WrappedComponent: React.ComponentType<T & PopupContextModel>
  ) =>
  (props: React.PropsWithChildren<T>) =>
    <PopupContext.Consumer>{(context) => <WrappedComponent {...props} {...context} />}</PopupContext.Consumer>;

export const DevsiteModalHeading = createComponent('div')({
  displayName: 'DevsiteModalHeading',
  Component: (props, ref) => <Canvas.Modal.Heading ref={ref} {...props} wordBreak='break-word' />,
});

export const DevsiteModalLowEmphasis = createComponent('span')({
  displayName: 'DevsiteModalLowEmphasis',
  Component: (props: BoxProps, ref, Element) => (
    <Box ref={ref} as={Element} fontWeight={system.fontWeight.medium} color={WcpStyles.color.text.hint} {...props} />
  ),
});
